/**
 * Organization Service
 * Handles organization-related API calls
 */

import { getBackendUrl } from '../config/config.loader';
import { httpClient, ApiResponse } from './http.client';

export interface Organization {
  // Core Identity
  id: string;
  name: string;
  companyCode?: string;
  
  // Business Classification  
  companyType?: 'general-contractor' | 'subcontractor' | 'developer' | 'architect' | 'engineering' | 'other';
  industry?: 'residential' | 'commercial' | 'industrial' | 'infrastructure' | 'civil' | 'renovation';
  companySize?: '1-10' | '11-50' | '51-200' | '201-500' | '500+';
  employeeCount?: number;
  founded?: string;
  businessType?: 'LLC' | 'CORPORATION' | 'PARTNERSHIP' | 'SOLE_PROPRIETORSHIP';
  
  // Address Information
  address?: string;             // Full address
  city?: string;               // City component  
  state?: string;              // State/Province
  zipCode?: string;            // ZIP/Postal Code
  country?: string;            // Operating country
  countryOfRegistration?: string; // Legal registration country
  
  // Contact Details
  phone?: string;
  email?: string;
  website?: string;
  primaryContact?: string;
  billingEmail?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  
  // Legal & Registration
  registrationNumber?: string;
  taxId?: string;
  licenseNumber?: string;
  certifications?: string[];
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  safetyRating?: string;
  
  // Business Operations
  specialties?: string;
  
  // Subscription & Limits
  subscriptionPlan?: 'FREE' | 'PROFESSIONAL' | 'ENTERPRISE';
  maxUsers?: number;
  currentUsers?: number;
  maxProjects?: number;
  storageLimit?: number;        // In GB
  storageUsed?: number;         // In GB
  apiCallsLimit?: number;
  apiCallsUsed?: number;
  
  // System Settings
  timezone?: string;
  currency?: string;
  dateFormat?: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
  language?: string;
  
  // Status & Metadata
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

class OrganizationService {
  /**
   * Get organization details by ID
   */
  async getOrganization(orgId: string, accessToken?: string): Promise<Organization> {
    try {
      const response = await httpClient.get<any>(`/api/v1/organizations/${orgId}`);
      
      const orgData = response.data;
      
      // Transform backend response to Organization format
      return {
        // Core Identity
        id: orgData.organization_id || orgData.id,
        name: orgData.org_name || orgData.name,
        companyCode: orgData.company_code,
        
        // Business Classification
        companyType: orgData.company_type,
        industry: orgData.industry,
        companySize: orgData.company_size,
        employeeCount: orgData.employee_count,
        founded: orgData.founded,
        businessType: orgData.business_type,
        
        // Address Information
        address: orgData.address,
        city: orgData.city,
        state: orgData.state,
        zipCode: orgData.zip_code,
        country: orgData.country,
        countryOfRegistration: orgData.country_of_registration,
        
        // Contact Details
        phone: orgData.phone,
        email: orgData.email,
        website: orgData.website,
        primaryContact: orgData.primary_contact,
        billingEmail: orgData.billing_email,
        emergencyContact: orgData.emergency_contact,
        emergencyPhone: orgData.emergency_phone,
        
        // Legal & Registration
        registrationNumber: orgData.registration_number,
        taxId: orgData.tax_id,
        licenseNumber: orgData.license_number,
        certifications: orgData.certifications,
        insuranceProvider: orgData.insurance_provider,
        insurancePolicyNumber: orgData.insurance_policy_number,
        safetyRating: orgData.safety_rating,
        
        // Business Operations
        specialties: orgData.specialties,
        
        // Subscription & Limits
        subscriptionPlan: orgData.subscription_plan,
        maxUsers: orgData.max_users,
        currentUsers: orgData.current_users,
        maxProjects: orgData.max_projects,
        storageLimit: orgData.storage_limit,
        storageUsed: orgData.storage_used,
        apiCallsLimit: orgData.api_calls_limit,
        apiCallsUsed: orgData.api_calls_used,
        
        // System Settings
        timezone: orgData.timezone,
        currency: orgData.default_currency || orgData.currency,
        dateFormat: orgData.date_format,
        language: orgData.language,
        
        // Status & Metadata
        isActive: orgData.is_active,
        createdAt: orgData.created_at,
        updatedAt: orgData.updated_at
      };
    } catch (error) {
      console.error('Failed to fetch organization:', error);
      throw error;
    }
  }

  /**
   * Get all organizations (for admin users)
   */
  async getOrganizations(): Promise<Organization[]> {
    try {
      const response = await httpClient.get<any[]>('/api/v1/organizations/');
      
      // Transform array of organizations
      return response.data.map((orgData: any) => ({
        id: orgData.organization_id || orgData.id,
        name: orgData.org_name || orgData.name,
        industry: orgData.industry,
        address: orgData.address,
        phone: orgData.phone,
        email: orgData.email,
        website: orgData.website,
        registrationNumber: orgData.registration_number,
        taxId: orgData.tax_id,
        primaryContact: orgData.primary_contact,
        countryOfRegistration: orgData.country_of_registration,
        licenseNumber: orgData.license_number,
        companyCode: orgData.company_code,
        specialties: orgData.specialties,
        subscriptionPlan: orgData.subscription_plan,
        maxUsers: orgData.max_users,
        maxProjects: orgData.max_projects,
        isActive: orgData.is_active,
        createdAt: orgData.created_at,
        updatedAt: orgData.updated_at
      }));
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      throw error;
    }
  }

  /**
   * Get current user's organization
   */
  async getMyOrganization(): Promise<Organization> {
    try {
      const response = await httpClient.get<any>('/api/v1/organizations/me');
      const orgData = response.data;
      
      return {
        id: orgData.organization_id || orgData.id,
        name: orgData.org_name || orgData.name,
        companyCode: orgData.company_code,
        companyType: orgData.company_type,
        industry: orgData.industry,
        companySize: orgData.company_size,
        employeeCount: orgData.employee_count,
        founded: orgData.founded,
        businessType: orgData.business_type,
        address: orgData.address,
        city: orgData.city,
        state: orgData.state,
        zipCode: orgData.zip_code,
        country: orgData.country,
        countryOfRegistration: orgData.country_of_registration,
        phone: orgData.phone,
        email: orgData.email,
        website: orgData.website,
        primaryContact: orgData.primary_contact,
        billingEmail: orgData.billing_email,
        emergencyContact: orgData.emergency_contact,
        emergencyPhone: orgData.emergency_phone,
        registrationNumber: orgData.registration_number,
        taxId: orgData.tax_id,
        licenseNumber: orgData.license_number,
        certifications: orgData.certifications,
        insuranceProvider: orgData.insurance_provider,
        insurancePolicyNumber: orgData.insurance_policy_number,
        safetyRating: orgData.safety_rating,
        specialties: orgData.specialties,
        subscriptionPlan: orgData.subscription_plan,
        maxUsers: orgData.max_users,
        currentUsers: orgData.current_users,
        maxProjects: orgData.max_projects,
        storageLimit: orgData.storage_limit,
        storageUsed: orgData.storage_used,
        apiCallsLimit: orgData.api_calls_limit,
        apiCallsUsed: orgData.api_calls_used,
        timezone: orgData.timezone,
        currency: orgData.currency,
        dateFormat: orgData.date_format,
        language: orgData.language,
        isActive: orgData.is_active,
        createdAt: orgData.created_at,
        updatedAt: orgData.updated_at
      };
    } catch (error) {
      console.error('Failed to fetch my organization:', error);
      throw error;
    }
  }

  /**
   * Update current user's organization
   */
  async updateMyOrganization(updateData: Partial<Organization>): Promise<Organization> {
    try {
      const backendData = this.transformToBackendFormat(updateData);
      const response = await httpClient.put<any>('/api/v1/organizations/me', backendData);
      
      return this.transformFromBackendFormat(response.data);
    } catch (error) {
      console.error('Failed to update my organization:', error);
      throw error;
    }
  }

  /**
   * Get organization by company code
   */
  async getOrganizationByCode(companyCode: string): Promise<Organization> {
    try {
      const response = await httpClient.get<any>(`/api/v1/organizations/by-code/${companyCode}`);
      return this.transformFromBackendFormat(response.data);
    } catch (error) {
      console.error('Failed to fetch organization by code:', error);
      throw error;
    }
  }

  /**
   * Get organization statistics
   */
  async getOrganizationStats(orgId?: string): Promise<any> {
    try {
      const endpoint = orgId 
        ? `/api/v1/organizations/${orgId}/stats`
        : '/api/v1/organizations/me/stats';
      
      const response = await httpClient.get<any>(endpoint);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch organization stats:', error);
      throw error;
    }
  }

  /**
   * Create new organization
   */
  async createOrganization(orgData: Partial<Organization>): Promise<Organization> {
    try {
      const backendData = this.transformToBackendFormat(orgData);
      const response = await httpClient.post<any>('/api/v1/organizations/', backendData);
      
      return this.transformFromBackendFormat(response.data);
    } catch (error) {
      console.error('Failed to create organization:', error);
      throw error;
    }
  }

  /**
   * Delete organization
   */
  async deleteOrganization(orgId: string): Promise<void> {
    try {
      await httpClient.delete<void>(`/api/v1/organizations/${orgId}`);
    } catch (error) {
      console.error('Failed to delete organization:', error);
      throw error;
    }
  }

  /**
   * Transform frontend data to backend format
   */
  private transformToBackendFormat(data: Partial<Organization>): any {
    return {
      org_name: data.name,
      company_code: data.companyCode,
      company_type: data.companyType,
      industry: data.industry,
      company_size: data.companySize,
      employee_count: data.employeeCount,
      founded: data.founded,
      business_type: data.businessType,
      address: data.address,
      city: data.city,
      state: data.state,
      zip_code: data.zipCode,
      country: data.country,
      country_of_registration: data.countryOfRegistration,
      phone: data.phone,
      email: data.email,
      website: data.website,
      primary_contact: data.primaryContact,
      billing_email: data.billingEmail,
      emergency_contact: data.emergencyContact,
      emergency_phone: data.emergencyPhone,
      registration_number: data.registrationNumber,
      tax_id: data.taxId,
      license_number: data.licenseNumber,
      certifications: data.certifications,
      insurance_provider: data.insuranceProvider,
      insurance_policy_number: data.insurancePolicyNumber,
      safety_rating: data.safetyRating,
      specialties: data.specialties,
      subscription_plan: data.subscriptionPlan,
      max_users: data.maxUsers,
      current_users: data.currentUsers,
      max_projects: data.maxProjects,
      storage_limit: data.storageLimit,
      storage_used: data.storageUsed,
      api_calls_limit: data.apiCallsLimit,
      api_calls_used: data.apiCallsUsed,
      timezone: data.timezone,
      currency: data.currency,
      date_format: data.dateFormat,
      language: data.language,
      is_active: data.isActive
    };
  }

  /**
   * Transform backend data to frontend format
   */
  private transformFromBackendFormat(orgData: any): Organization {
    return {
      id: orgData.organization_id || orgData.id,
      name: orgData.org_name || orgData.name,
      companyCode: orgData.company_code,
      companyType: orgData.company_type,
      industry: orgData.industry,
      companySize: orgData.company_size,
      employeeCount: orgData.employee_count,
      founded: orgData.founded,
      businessType: orgData.business_type,
      address: orgData.address,
      city: orgData.city,
      state: orgData.state,
      zipCode: orgData.zip_code,
      country: orgData.country,
      countryOfRegistration: orgData.country_of_registration,
      phone: orgData.phone,
      email: orgData.email,
      website: orgData.website,
      primaryContact: orgData.primary_contact,
      billingEmail: orgData.billing_email,
      emergencyContact: orgData.emergency_contact,
      emergencyPhone: orgData.emergency_phone,
      registrationNumber: orgData.registration_number,
      taxId: orgData.tax_id,
      licenseNumber: orgData.license_number,
      certifications: orgData.certifications,
      insuranceProvider: orgData.insurance_provider,
      insurancePolicyNumber: orgData.insurance_policy_number,
      safetyRating: orgData.safety_rating,
      specialties: orgData.specialties,
      subscriptionPlan: orgData.subscription_plan,
      maxUsers: orgData.max_users,
      currentUsers: orgData.current_users,
      maxProjects: orgData.max_projects,
      storageLimit: orgData.storage_limit,
      storageUsed: orgData.storage_used,
      apiCallsLimit: orgData.api_calls_limit,
      apiCallsUsed: orgData.api_calls_used,
      timezone: orgData.timezone,
      currency: orgData.currency,
      dateFormat: orgData.date_format,
      language: orgData.language,
      isActive: orgData.is_active,
      createdAt: orgData.created_at,
      updatedAt: orgData.updated_at
    };
  }

  /**
   * Update organization details
   */
  async updateOrganization(orgId: string, updateData: Partial<Organization>): Promise<Organization> {
    try {
      // Transform frontend format to backend format
      const backendData = {
        // Core Identity
        org_name: updateData.name,
        company_code: updateData.companyCode,
        
        // Business Classification
        company_type: updateData.companyType,
        industry: updateData.industry,
        company_size: updateData.companySize,
        employee_count: updateData.employeeCount,
        founded: updateData.founded,
        business_type: updateData.businessType,
        
        // Address Information
        address: updateData.address,
        city: updateData.city,
        state: updateData.state,
        zip_code: updateData.zipCode,
        country: updateData.country,
        country_of_registration: updateData.countryOfRegistration,
        
        // Contact Details
        phone: updateData.phone,
        email: updateData.email,
        website: updateData.website,
        primary_contact: updateData.primaryContact,
        billing_email: updateData.billingEmail,
        emergency_contact: updateData.emergencyContact,
        emergency_phone: updateData.emergencyPhone,
        
        // Legal & Registration
        registration_number: updateData.registrationNumber,
        tax_id: updateData.taxId,
        license_number: updateData.licenseNumber,
        certifications: updateData.certifications,
        insurance_provider: updateData.insuranceProvider,
        insurance_policy_number: updateData.insurancePolicyNumber,
        safety_rating: updateData.safetyRating,
        
        // Business Operations
        specialties: updateData.specialties,
        
        // Subscription & Limits
        subscription_plan: updateData.subscriptionPlan,
        max_users: updateData.maxUsers,
        current_users: updateData.currentUsers,
        max_projects: updateData.maxProjects,
        storage_limit: updateData.storageLimit,
        storage_used: updateData.storageUsed,
        api_calls_limit: updateData.apiCallsLimit,
        api_calls_used: updateData.apiCallsUsed,
        
        // System Settings
        timezone: updateData.timezone,
        default_currency: updateData.currency,
        date_format: updateData.dateFormat,
        language: updateData.language,
        
        // Status & Metadata
        is_active: updateData.isActive
      };

      const response = await httpClient.put<any>(`/api/v1/organizations/${orgId}`, backendData);
      
      const orgData = response.data;
      
      return {
        id: orgData.organization_id || orgData.id,
        name: orgData.org_name || orgData.name,
        industry: orgData.industry,
        address: orgData.address,
        phone: orgData.phone,
        email: orgData.email,
        website: orgData.website,
        registrationNumber: orgData.registration_number,
        taxId: orgData.tax_id,
        primaryContact: orgData.primary_contact,
        countryOfRegistration: orgData.country_of_registration,
        licenseNumber: orgData.license_number,
        companyCode: orgData.company_code,
        specialties: orgData.specialties,
        subscriptionPlan: orgData.subscription_plan,
        maxUsers: orgData.max_users,
        maxProjects: orgData.max_projects,
        isActive: orgData.is_active,
        createdAt: orgData.created_at,
        updatedAt: orgData.updated_at
      };
    } catch (error) {
      console.error('Failed to update organization:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const organizationService = new OrganizationService();